// Internal claims, not a client body or authorization proof. Data re-resolves permissions on every page.
import { IsoDateTime, Uuid } from "@secretaa/http-contract";
import { z } from "zod";
const Hash = z.string().regex(/^[a-f0-9]{64}$/);
const Id = Uuid.refine(id => id === id.toLowerCase());
export const Bindings = z.object({ workspaceId: Id, projectId: Id, subjectId: Id,
  scopeVersion: z.number().int().nonnegative(), scopeTopologyHash: Hash, manifestHash: Hash,
  canonicalizationVersion: z.number().int().positive(), plannerPolicyVersion: z.number().int().positive(),
  queryHash: Hash, limit: z.number().int().min(1).max(100),
}).strict();
export const Position = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("created"), t: IsoDateTime, id: Id }).strict(),
  z.object({ kind: z.literal("field"), n: z.boolean(), v: z.union([z.string().max(1024), z.boolean(), z.null()]), id: Id }).strict(),
]).refine(p => p.kind !== "field" || p.n === (p.v === null));
export const Payload = Bindings.extend({ position: Position, cursorVersion: z.literal(1), aud: z.literal("project-query"),
  iat: z.number().int().nonnegative(), exp: z.number().int().nonnegative(),
}).strict().refine(c => c.exp === c.iat + 900);
export type Bindings = z.infer<typeof Bindings>;
export type Position = z.infer<typeof Position>;
export type Payload = z.infer<typeof Payload>;

export function checkBinding(raw: unknown, current: Bindings, nowSeconds: number):
  { ok: true; position: Position } | { ok: false; reason: "QUERY_CURSOR_INVALID" | "QUERY_CURSOR_STALE" } {
  const invalid = { ok: false, reason: "QUERY_CURSOR_INVALID" } as const;
  const stale = { ok: false, reason: "QUERY_CURSOR_STALE" } as const;
  const parsed = Payload.safeParse(raw);
  if (!parsed.success || !Bindings.safeParse(current).success || !Number.isSafeInteger(nowSeconds) || nowSeconds < 0) return invalid;
  const p = parsed.data;
  if (p.iat > nowSeconds + 60 || p.workspaceId !== current.workspaceId || p.projectId !== current.projectId || p.subjectId !== current.subjectId) return invalid;
  if (p.scopeVersion !== current.scopeVersion || p.scopeTopologyHash !== current.scopeTopologyHash || p.manifestHash !== current.manifestHash
    || p.canonicalizationVersion !== current.canonicalizationVersion || p.plannerPolicyVersion !== current.plannerPolicyVersion) return stale;
  if (p.queryHash !== current.queryHash || p.limit !== current.limit) return invalid;
  if (nowSeconds >= p.exp + 60) return stale;
  return { ok: true, position: p.position };
}
